import axios from 'axios';
import config from '../config/config';

// 修复本地开发环境的 HTTPS 问题：强制使用 HTTP
let baseURL = config.api.baseURL;
if (baseURL && baseURL.includes('https://localhost')) {
  baseURL = baseURL.replace('https://localhost', 'http://localhost');
  console.warn('⚠️ 检测到 HTTPS localhost，已自动转换为 HTTP:', baseURL);
}

// 创建axios实例
const api = axios.create({
  baseURL: baseURL || 'http://localhost:3005',
  timeout: config.api.timeout,
  headers: config.api.headers,
  // 移动端网络优化
  maxRedirects: 3,
  maxContentLength: 10 * 1024 * 1024, // 10MB
  validateStatus: function (status) {
    return status >= 200 && status < 300;
  }
});

// 判断是否为 FormData
const isFormData = (data) =>
  typeof FormData !== 'undefined' && data instanceof FormData;

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (isFormData(config.data)) {
      delete config.headers['Content-Type'];
    } else if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 移动端网络重试机制
const retryRequest = (config, retryCount = 0) => {
  const maxRetries = 3;
  const retryDelay = Math.pow(2, retryCount) * 1000; // 指数退避
  
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      api(config)
        .then(resolve)
        .catch((error) => {
          if (retryCount < maxRetries && (
            error.code === 'NETWORK_ERROR' || 
            error.code === 'ECONNABORTED' ||
            error.response?.status >= 500
          )) {
            console.log(`网络重试 ${retryCount + 1}/${maxRetries}`);
            retryRequest(config, retryCount + 1).then(resolve).catch(reject);
          } else {
            reject(error);
          }
        });
    }, retryDelay);
  });
};

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 详细错误日志
    console.error('API请求错误详情:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
      timeout: error.config?.timeout
    });
    
    // 处理不同类型的错误
    if (error.response?.status === 401) {
      console.error('认证失败，自动退出登录');
      // 清空认证数据并跳转
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.error('请求超时 - 服务器响应时间过长');
      // 移动端网络重试
      if (error.config && !error.config._retry) {
        error.config._retry = true;
        return retryRequest(error.config);
      }
    } else if (error.code === 'ERR_NETWORK') {
      console.error('网络连接错误 - 无法连接到服务器');
      // 移动端网络重试
      if (error.config && !error.config._retry) {
        error.config._retry = true;
        return retryRequest(error.config);
      }
    } else if (error.code === 'ERR_CONNECTION_REFUSED') {
      console.error('连接被拒绝 - 服务器可能未启动');
    } else if (error.response?.status >= 500) {
      console.error('服务器内部错误:', error.response.status, error.response.statusText);
    } else if (error.response?.status >= 400) {
      console.error('客户端请求错误:', error.response.status, error.response.statusText);
    }
    
    return Promise.reject(error);
  }
);

// API方法
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout')
};

export const memoAPI = {
  getAll: (params) => api.get('/memos', { params }),
  create: (data) => api.post('/memos', data),
  update: (id, data) => api.put(`/memos/${id}`, data),
  delete: (id) => api.delete(`/memos/${id}`),
  getStats: () => api.get('/stats'),
  getRankings: () => api.get('/rankings'),
  getServerTime: () => api.get('/server-time'),
  exportCSV: () => api.get('/export-csv', { responseType: 'blob' })
};

export const userAPI = {
  getUsers: (timestamp) => api.get(`/auth/users${timestamp ? `?t=${timestamp}` : ''}`),
  createUser: (userData) => api.post('/auth/users', userData),
  updateUser: (id, userData) => api.put(`/auth/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/auth/users/${id}`),
  approveUser: (id) => api.put(`/auth/users/${id}/approve`),
  generateGoogleSecret: (userId) => api.get(`/auth/users/${userId}/google-secret`),
  enableGoogleAuth: (userId, data) => api.post(`/auth/users/${userId}/enable-google-auth`, data),
  disableGoogleAuth: (userId) => api.post(`/auth/users/${userId}/disable-google-auth`),
  setSuperAdmin: (userId, isSuperAdmin) => api.put(`/auth/users/${userId}/super-admin`, { isSuperAdmin }),
  setUserPassword: (userId, userPassword) => api.put(`/auth/users/${userId}/user-password`, { userPassword }),
  resetUserPassword: (userId) => api.post(`/auth/users/${userId}/reset-user-password`)
};

export const loginLogAPI = {
  getLoginLogs: (params) => api.get('/auth/login-logs', { params }),
  getMyLoginLogs: (params) => api.get('/auth/my-login-logs', { params })
};

export const monitorAPI = {
  check: (payload) => api.post('/monitor/check', payload)
};

export const aiAPI = {
  ask: (payload, options = {}) => {
    const headers = options.headers || {};
    return api.post('/ai/ask', payload, { headers });
  },
  generateImage: (payload) => api.post('/ai/generate-image', payload, { timeout: 120000 }),
  code: (payload) => api.post('/ai/code', payload),
  stream: (payload) => api.post('/ai/stream', payload, { responseType: 'stream' }),
  getModels: () => api.get('/ai/models'),
  uploadFile: (formData) => api.post('/ai/upload-file', formData, { 
    timeout: 120000,
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  readFile: (formData) => api.post('/ai/read-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  chat: {
    listSessions: (params) => api.get('/ai/chat/sessions', { params }),
    createSession: (payload) => api.post('/ai/chat/sessions', payload),
    updateSession: (id, payload) => api.patch(`/ai/chat/sessions/${id}`, payload),
    archiveSession: (id) => api.delete(`/ai/chat/sessions/${id}`),
    getMessages: (id) => api.get(`/ai/chat/sessions/${id}/messages`),
    sendMessage: (id, payload) => api.post(`/ai/chat/sessions/${id}/messages`, payload),
    uploadFiles: (formData) => api.post('/ai/chat/files', formData, {
      timeout: 120000
    }),
    generateImage: (id, payload) => api.post(`/ai/chat/sessions/${id}/images`, payload, { timeout: 120000 }),
    listPlugins: () => api.get('/ai/chat/plugins'),
    createPlugin: (payload) => api.post('/ai/chat/plugins', payload),
    updatePlugin: (id, payload) => api.patch(`/ai/chat/plugins/${id}`, payload),
    deletePlugin: (id) => api.delete(`/ai/chat/plugins/${id}`)
  },
  // SEO功能
  seo: {
    generatePages: (payload) => api.post('/ai/seo/generate-pages', payload, { timeout: 180000 }),
    generateSchema: (payload) => api.post('/ai/seo/schema', payload, { timeout: 60000 }),
    generateMetaTags: (payload) => api.post('/ai/seo/meta-tags', payload, { timeout: 60000 }),
    analyze: (payload) => api.post('/ai/seo/analyze', payload, { timeout: 120000 }),
    fullPackage: (payload) => api.post('/ai/seo/full-package', payload, { timeout: 300000 })
  }
};

// 系统设置API
export const systemAPI = {
  getSystemInfo: () => api.get('/system/info'),
  getAppConfig: () => api.get('/system/config'),
  getDatabaseStatus: () => api.get('/system/database'),
  getLogFiles: () => api.get('/system/logs'),
  clearCache: () => api.post('/system/clear-cache'),
  restartApp: () => api.post('/system/restart')
};

export default api; 